import { useEffect, useState } from "react";
import DashSidebar from "@/components/DashSidebar";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";

export default function AdminQuiz() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/quiz/config");
      setQuestions(data.questions || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/quiz/config", { questions });
      toast.success("Quiz updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const updateQ = (idx, field, value) => {
    const next = [...questions];
    next[idx] = { ...next[idx], [field]: value };
    setQuestions(next);
  };

  const updateOpt = (qi, oi, field, value) => {
    const next = [...questions];
    const opts = [...(next[qi].options || [])];
    opts[oi] = { ...opts[oi], [field]: value };
    next[qi] = { ...next[qi], options: opts };
    setQuestions(next);
  };

  const addQ = () => {
    setQuestions([
      ...questions,
      { id: `q_${Date.now()}`, label: "New question", type: "single", required: true, options: [{ value: "opt1", label: "Option 1" }] },
    ]);
  };

  const delQ = (idx) => setQuestions(questions.filter((_, i) => i !== idx));

  const addOpt = (qi) => {
    const next = [...questions];
    const opts = [...(next[qi].options || []), { value: `opt_${Date.now()}`, label: "New option" }];
    next[qi] = { ...next[qi], options: opts };
    setQuestions(next);
  };

  const delOpt = (qi, oi) => {
    const next = [...questions];
    next[qi] = { ...next[qi], options: next[qi].options.filter((_, i) => i !== oi) };
    setQuestions(next);
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions];
    [next[idx], next[j]] = [next[j], next[idx]];
    setQuestions(next);
  };

  return (
    <div className="flex min-h-screen bg-[#F7F5F0]">
      <DashSidebar role="admin" />
      <main className="flex-1 lg:ml-0 ml-64 p-8 md:p-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#1A2E24]">Quiz configuration</h1>
            <p className="text-[#5C6B62] mt-1">Edit questions & answer options.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={addQ} variant="outline" className="rounded-xl h-11 border-[#E2DFD8]" data-testid="quiz-add-question">
              <Plus className="w-4 h-4 mr-2" /> Add question
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#2C4C3B] hover:bg-[#1A2E24] rounded-xl h-11" data-testid="quiz-save">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>

        {loading ? <div className="text-[#5C6B62]">Loading…</div> : (
          <div className="space-y-5">
            {questions.map((q, qi) => (
              <div key={qi} className="card-soft p-6" data-testid={`quiz-q-${qi}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="flex flex-col gap-1 pt-2 text-[#5C6B62]">
                    <button onClick={() => move(qi, -1)} className="hover:text-[#2C4C3B] text-xs">▲</button>
                    <button onClick={() => move(qi, 1)} className="hover:text-[#2C4C3B] text-xs">▼</button>
                  </div>
                  <div className="flex-1 space-y-3">
                    <Input
                      value={q.label}
                      onChange={(e) => updateQ(qi, "label", e.target.value)}
                      className="h-11 rounded-xl bg-[#F7F5F0] border-[#E2DFD8] font-semibold"
                      placeholder="Question label"
                    />
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        value={q.id}
                        onChange={(e) => updateQ(qi, "id", e.target.value)}
                        className="h-10 rounded-xl bg-[#F7F5F0] border-[#E2DFD8] text-sm"
                        placeholder="question_id (e.g. memory_concerns)"
                      />
                      <select
                        value={q.type}
                        onChange={(e) => updateQ(qi, "type", e.target.value)}
                        className="h-10 rounded-xl bg-[#F7F5F0] border border-[#E2DFD8] px-3 text-sm"
                      >
                        <option value="single">Single choice</option>
                      </select>
                      <label className="flex items-center gap-2 text-sm text-[#2C4C3B]">
                        <input
                          type="checkbox"
                          checked={!!q.required}
                          onChange={(e) => updateQ(qi, "required", e.target.checked)}
                          className="w-4 h-4"
                        />
                        Required
                      </label>
                    </div>
                  </div>
                  <button onClick={() => delQ(qi)} className="text-[#E07A5F] hover:text-[#C96B52] p-2" data-testid={`quiz-delete-q-${qi}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="pl-8 space-y-2">
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex gap-2 items-center">
                      <Input
                        value={opt.value}
                        onChange={(e) => updateOpt(qi, oi, "value", e.target.value)}
                        className="h-10 rounded-xl bg-[#F7F5F0] border-[#E2DFD8] text-sm max-w-[180px]"
                        placeholder="value"
                      />
                      <Input
                        value={opt.label}
                        onChange={(e) => updateOpt(qi, oi, "label", e.target.value)}
                        className="h-10 rounded-xl bg-[#F7F5F0] border-[#E2DFD8] text-sm flex-1"
                        placeholder="display label"
                      />
                      <button onClick={() => delOpt(qi, oi)} className="text-[#E07A5F] p-2"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => addOpt(qi)} className="text-sm text-[#2C4C3B] hover:underline mt-1">+ Add option</button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 card-soft p-5 bg-[#9CAF88]/5">
          <h4 className="font-display text-sm font-semibold text-[#2C4C3B] mb-2">Scoring logic reference</h4>
          <p className="text-xs text-[#5C6B62] leading-relaxed">
            The built-in scoring reads these question IDs: <strong>memory_concerns</strong>, <strong>independence_level</strong>, <strong>mobility</strong>, <strong>daily_help</strong>.
            Use values <em>yes / significant</em> (memory) → Memory Care; moderate independence/mobility/daily-help → Assisted Living; otherwise Independent Living.
          </p>
        </div>
      </main>
    </div>
  );
}
